/**
 * Restock Notification Handler for storefront button-style CTAs.
 * Uses the shared optimistic toggle service so cards and other
 * restock buttons stay in sync and remain responsive.
 */
(() => {
  'use strict';

  if (window.__MM_RESTOCK_CARD_BOOTSTRAPPED__) return;
  window.__MM_RESTOCK_CARD_BOOTSTRAPPED__ = true;

  const service = window.MMRestockToggleService;
  if (!service) {
    console.warn('[restock-card] Missing MMRestockToggleService');
    return;
  }

  const CTA_SELECTOR = '[data-mm-restock-cta]';
  const BUTTON_SELECTOR = '[data-mm-restock-btn]';
  const LABEL_SELECTOR = '[data-mm-restock-label]';
  const STATUS_SELECTOR = '[data-mm-restock-status]';

  function getVariantId(cta) {
    return service.normalizeVariantId(cta?.getAttribute('data-variant-id'));
  }

  function getApiBase(cta) {
    return service.normalizeApiBase(cta?.getAttribute('data-app-proxy-base') || '/apps/garden');
  }

  function getCustomerEmail(cta) {
    if (!cta || typeof cta.getAttribute !== 'function') return '';
    return String(cta.getAttribute('data-customer-email') || '').trim();
  }

  function getRestockText(cta, name, fallback) {
    if (!cta || typeof cta.getAttribute !== 'function') return fallback;
    return String(cta.getAttribute(name) || '').trim() || fallback;
  }

  function getLoginUrl(cta) {
    return getRestockText(
      cta,
      'data-login-url',
      '/account/login?return_url=' + encodeURIComponent(window.location.pathname + window.location.search),
    );
  }

  function clearStatus(statusEl) {
    if (!statusEl) return;
    statusEl.textContent = '';
    statusEl.hidden = true;
  }

  function showSignInToast(cta) {
    service.showThemeToast(
      getRestockText(cta, 'data-login-text', 'Please sign in to get notified when this plant is back in stock.'),
      'info',
      {
        linkText: 'Sign in',
        linkHref: getLoginUrl(cta),
      },
    );
  }

  const ctaRegistry = new WeakMap();
  const activeCtas = new Set();

  function teardownRestockButton(cta) {
    if (!cta) return;
    const record = ctaRegistry.get(cta);
    if (!record) return;

    try {
      record.controller?.destroy?.();
    } catch (error) {
      console.warn('[restock-card] controller.destroy failed:', error);
    }

    if (record.button && record.clickHandler) {
      record.button.removeEventListener('click', record.clickHandler);
      record.button.removeAttribute('data-restock-card-initialized');
    }

    ctaRegistry.delete(cta);
    activeCtas.delete(cta);
  }

  function setupRestockButton(cta) {
    if (!cta) return;

    const button = cta.querySelector(BUTTON_SELECTOR);
    if (!button || button.hasAttribute('data-restock-card-initialized')) return;
    button.setAttribute('data-restock-card-initialized', 'true');

    const statusEl = cta.querySelector(STATUS_SELECTOR);
    const productGid = cta.getAttribute('data-product-gid');
    if (!productGid) return;

    const getInitialSubscribed = () => {
      return (
        service.readPersistedSubscriptionHint?.(productGid, getVariantId(cta)) ||
        button.getAttribute('aria-pressed') === 'true'
      );
    };

    const controller = service.createProductToggleController({
      apiBase: () => getApiBase(cta),
      productGid: () => cta.getAttribute('data-product-gid'),
      variantId: () => getVariantId(cta),
      customerEmail: () => getCustomerEmail(cta),
      initialSubscribed: getInitialSubscribed,
      ringTarget: ({ subscribed }) => service.resolveBellTarget(button, subscribed),
      render: ({ subscribed, pending, subscriptionState }) => {
        service.applyButtonSubscribedState(button, subscribed, {
          labelSelector: LABEL_SELECTOR,
          subscriptionState,
        });

        if (pending) {
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('data-loading', 'true');
        } else {
          button.removeAttribute('aria-busy');
          button.setAttribute('data-loading', 'false');
        }
      },
      onOptimisticChange: ({ subscribed }) => {
        clearStatus(statusEl);
        service.writePersistedSubscriptionHint?.(productGid, getVariantId(cta), subscribed);

        const toastKey = `restock-card::${productGid}::${getVariantId(cta) || ''}`;
        const scheduleToast =
          typeof service.scheduleRestockToast === 'function'
            ? service.scheduleRestockToast.bind(service)
            : (_key, message, type, options) => service.showThemeToast(message, type, options);

        if (subscribed) {
          scheduleToast(
            toastKey,
            getRestockText(cta, 'data-success-text', "You'll be the first to know when this product is back in stock!"),
            'success',
            {
              linkText: 'View notifications',
              linkHref: '/account#notifications',
            },
          );
        } else {
          scheduleToast(toastKey, getRestockText(cta, 'data-removed-text', 'Notification removed.'), 'info', {});
        }
      },
      onSuccess: () => {
        clearStatus(statusEl);
      },
      onUnauthorized: () => {
        showSignInToast(cta);
      },
      onError: (error) => {
        service.showThemeToast(
          error?.message || getRestockText(cta, 'data-error-text', 'Unable to save your alert. Please try again.'),
          'error',
        );
      },
      onUnhandledError: (error) => {
        console.error('[restock-card] Toggle failed:', error);
      },
      subscribedClickBehavior: 'remove-all-sources',
      requestSource: getRestockText(cta, 'data-restock-request-source', 'product-card'),
      traceSurface: getRestockText(cta, 'data-restock-trace-surface', 'product-card'),
      traceComponent: getRestockText(cta, 'data-restock-trace-component', 'restock-card'),
    });

    const clickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearStatus(statusEl);
      console.log('[RestockCard:TRACE] button-click', {
        productGid: cta.getAttribute('data-product-gid'),
        variantId: getVariantId(cta),
        hasCustomerSession: Boolean(
          String(window.ShopifyCustomer?.id || window.Shopify?.customer?.id || '').trim()
          || getCustomerEmail(cta),
        ),
      });

      const action = controller.toggle(event);
      action?.catch?.(() => {});
    };
    button.addEventListener('click', clickHandler);

    ctaRegistry.set(cta, { controller, button, clickHandler });
    activeCtas.add(cta);
  }

  function initializeAll(root = document) {
    if (!root) return;

    if (root.nodeType === 1 && root.matches && root.matches(CTA_SELECTOR)) {
      setupRestockButton(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(CTA_SELECTOR).forEach(setupRestockButton);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  const queuedRoots = new Set();
  let queueTimer = null;

  function flushQueuedInitializers() {
    queueTimer = null;
    queuedRoots.forEach((root) => initializeAll(root));
    queuedRoots.clear();
  }

  function queueInitialize(root) {
    if (!root || root.nodeType !== 1) return;
    queuedRoots.add(root);
    if (queueTimer) return;

    if (typeof window.requestAnimationFrame === 'function') {
      queueTimer = window.requestAnimationFrame(flushQueuedInitializers);
    } else {
      queueTimer = window.setTimeout(flushQueuedInitializers, 16);
    }
  }

  function cancelPendingInitialize(node) {
    if (!node) return;
    if (queuedRoots.has(node)) {
      queuedRoots.delete(node);
    }
  }

  function teardownRemovedNode(node) {
    if (!node || node.nodeType !== 1) return;

    cancelPendingInitialize(node);

    if (node.matches?.(CTA_SELECTOR)) {
      teardownRestockButton(node);
    }

    if (typeof node.querySelectorAll === 'function') {
      node.querySelectorAll(CTA_SELECTOR).forEach(teardownRestockButton);
    }
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.removedNodes && mutation.removedNodes.length) {
        mutation.removedNodes.forEach(teardownRemovedNode);
      }
      if (mutation.addedNodes && mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => queueInitialize(node));
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.RestockCard = {
    init: initializeAll,
    setup: setupRestockButton,
    teardown: teardownRestockButton,
  };
})();
